import { useState } from 'react'
import { exportMonth, monthOptions } from '@core/export'
import { Button, Card, ErrorNote } from '@design/components'

/**
 * A month of paperwork, in one file.
 *
 * Not for an accountant — this family does not send one anything. The reason
 * it earns its place is narrower and better: ten years of documents must not
 * be trapped inside one app. One tap and the month is a folder of ordinary
 * files with readable names, on a disk you control, whatever happens to this
 * project.
 *
 * The download is built entirely on the phone: the originals are fetched
 * through the same short-lived signed URLs the viewer uses, zipped in memory,
 * and handed to the browser. Nothing new is stored, and no server has to hold
 * a copy of the family's year while it works.
 *
 * The progress line matters more than it looks — a month is dozens of
 * documents over a restaurant's wifi, and a button that sits silent for forty
 * seconds gets pressed again.
 */
export function ExportSetting() {
  const months = monthOptions()
  const [month, setMonth] = useState(months[1]?.value ?? months[0]!.value)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true)
    setError('')
    setDone(null)
    setProgress('Verzamelen…')
    try {
      const result = await exportMonth(month, (p) =>
        setProgress(`${p.done} van ${p.total}`),
      )

      const url = URL.createObjectURL(result.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = result.filename
      link.click()
      // Revoked on the next tick, not immediately: Safari has not finished
      // reading the blob when click() returns.
      setTimeout(() => URL.revokeObjectURL(url), 30_000)

      setDone(
        result.missing > 0
          ? `${result.documents} bestanden. ${result.missing} betaling(en) zonder document.`
          : `${result.documents} bestanden.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Exporteren lukte niet.')
    }
    setBusy(false)
    setProgress(null)
  }

  return (
    <Card className="mb-4">
      <p className="font-medium mb-1">Maand exporteren</p>
      <p className="text-sm text-muted mb-3">
        Alle documenten van een maand in één zip, met een overzicht in csv.
        Zodat je papieren nooit vastzitten in deze app.
      </p>

      <select
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        disabled={busy}
        className="w-full min-h-[3rem] px-3 mb-3 rounded-xl bg-canvas border border-line"
      >
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>

      <ErrorNote>{error}</ErrorNote>

      {done && <p className="text-sm text-ok mb-2">Klaar — {done}</p>}

      <Button tone="secondary" onClick={run} disabled={busy}>
        {busy ? (progress ?? 'Bezig…') : 'Downloaden'}
      </Button>
    </Card>
  )
}
